import moment from "moment";

export default {
  name: "Professional/Non-Professional Recruitment Information",
  pageInfo: {
    isVisible: {
      when: "occupationType",
      is: ["professional_occupation", "non_professional"],
      operator: "||",
    },
    text: `<b><u>Note:</u></b> In accordance with 20 CFR 656.17(e)(1)(i), two (2) of the steps—a job order and two print advertisements — are mandatory for all applications, except applications for college or university teachers selected in a competitive selection and recruitment process, as provided in 20 CFR 656.18.
    <br/><br/>
    <b>Note:</b> The mandatory recruitment steps for professional occupations must be conducted at least 30 days, but no more than 180 days, before filing the application.
    `,
    coloring: "grey",
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify the applicant's Occupation Type in Field H.b within the Preliminary Section of this application",
        type: "info",
        isVisible: {
          when: "occupationType",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you indicated that this application is NOT for either a professional or non-professional occupation, this section is not necessary.",
        type: "success",
        isVisible: {
          when: "occupationType",
          is: ["college_university_teacher"],
          operator: "||",
        },
      },
      {
        title: "Section Complete",
        description: `Because you indicated that this application is for a professional athlete, this section is not necessary.`,
        type: "success",
        isVisible: {
          when: "occupationType",
          is: ["professional_athlete"],
          operator: "compareMultiFieldValuesAND_ORInArray",
        },
      },
    ],
  },
  sections: [
    {
      isVisible: {
        when: "occupationType",
        is: ["professional_occupation", "non_professional"],
        operator: "||",
      },
      fields: [
        {
          code: "H.c.1a",
          machine_name: "swaJobOrderStartDate",
          widget: "date",
          label: "Start date of SWA job order §",
          maxDate: "swaJobOrderEndDate",
          warnings: [
            {
              text: "The recruitment steps must be conducted at least 30 days, but no more than 180 days, before filing the application.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.swaJobOrderStartDate;
                  const errors = formApi.getState().errors.swaJobOrderStartDate;
                  if (!!value && !errors) {
                    const today = moment();
                    const dateToCheck = moment(value);
                    const diff = Math.round(
                      today.diff(dateToCheck, "days", true)
                    );
                    return diff >= 180 || diff < 30;
                  }
                  return false;
                },
                is: "swaJobOrderStartDateCheck",
                operator: "customValidation",
              },
            },
            {
              text: "The duration of start to end date must be at least 30 days",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.swaJobOrderStartDate;
                  const valueToCheck =
                    formApi.getState().values.swaJobOrderEndDate;
                  const errors = formApi.getState().errors.swaJobOrderStartDate;
                  if (!!value && !errors) {
                    const start = moment(value);
                    const end = moment(valueToCheck);
                    const diff = Math.round(end.diff(start, "days", true)) + 1;
                    return diff < 30;
                  }
                  return false;
                },
                is: "swaJobOrderStartDateCheck",
                operator: "customValidation",
              },
            },
          ],
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: ["professional_occupation", "non_professional"],
              },
            },
            {
              dateStartEnd: "swaJobOrderEndDate",
              message: "Begin Date must occur before End Date.",
              needsFieldValues: true,
              extraData: {
                type: "start",
              },
            },
          ],
          tooltip:
            "Enter the start date for the State Workforce Agency job order in MM/DD/YYYY format",
        },
        {
          code: "H.c.1b",
          machine_name: "swaJobOrderEndDate",
          widget: "date",
          label: "End date of SWA job order §",
          minDate: "swaJobOrderStartDate",
          warnings: [
            {
              text: "The recruitment steps must be conducted at least 30 days, but no more than 180 days, before filing the application.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.swaJobOrderEndDate;
                  const errors = formApi.getState().errors.swaJobOrderEndDate;
                  if (!!value && !errors) {
                    const today = moment();
                    const dateToCheck = moment(value);
                    const diff = Math.round(
                      today.diff(dateToCheck, "days", true)
                    );
                    return diff >= 180 || diff < 30;
                  }
                  return false;
                },
                is: "swaJobOrderEndDateCheck",
                operator: "customValidation",
              },
            },
            {
              text: "The duration of start to end date must be at least 30 days",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.swaJobOrderStartDate;
                  const valueToCheck =
                    formApi.getState().values.swaJobOrderEndDate;
                  const errors = formApi.getState().errors.swaJobOrderStartDate;
                  if (!!value && !errors) {
                    const start = moment(value);
                    const end = moment(valueToCheck);
                    const diff = Math.round(end.diff(start, "days", true)) + 1;
                    return diff < 30;
                  }
                  return false;
                },
                is: "swaJobOrderStartDateCheck",
                operator: "customValidation",
              },
            },
          ],
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: ["professional_occupation", "non_professional"],
              },
            },
            {
              dateStartEnd: "swaJobOrderStartDate",
              message: "End Date must occur after Begin Date.",
              needsFieldValues: true,
              extraData: {
                type: "end",
              },
            },
          ],
          tooltip:
            "Enter the end date for the State Workforce Agency job order in MM/DD/YYYY format.",
        },
        {
          isGroup: {
            code: "H.c.2",
            machine_name: "sundayPaperExists",
            label:
              "Is there a Sunday edition of a newspaper (of general circulation) in the area of intended employment? §",
            clearBtn: {
              clearSchema: [],
            },
            tooltip: `Indicate if there is a Sunday edition of the newspaper in the area of intended employment by marking “Yes” or “No.”
              <br/><br/><b><u>Note:</u></b> If the job opportunity is located in a rural area of 
              intended employment that does not have a newspaper with a Sunday edition, the employer may use the 
              edition with the widest circulation in the area of intended employment, in accordance with 20 CFR 656.17(e)(1)(i)(B)(2).`,
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "yes",
                validate: [
                  {
                    isRequired: "occupationType",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: ["professional_occupation", "non_professional"],
                    },
                  },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: "no",
                validate: [
                  {
                    isRequired: "occupationType",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: ["professional_occupation", "non_professional"],
                    },
                  },
                ],
              },
            ],
          },
        },
        {
          code: "H.c.2a",
          machine_name: "newspaperNameForAd",
          widget: "text",
          label:
            "Name of the newspaper of general circulation in which an advertisement was placed §",
          maxLength: 35,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: ["professional_occupation", "non_professional"],
              },
            },
          ],
          tooltip:
            "Enter the name of the newspaper in which the first advertisement was placed.",
        },
        {
          code: "H.c.2b",
          machine_name: "newspaperAdPlacedDate",
          widget: "date",
          label: "Advertisement date §",
          warnings: [
            {
              text: "The recruitment steps must be conducted at least 30 days, but no more than 180 days, before filing the application.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.newspaperAdPlacedDate;
                  const errors =
                    formApi.getState().errors.newspaperAdPlacedDate;
                  if (!!value && !errors) {
                    const today = moment();
                    const dateToCheck = moment(value);
                    const diff = Math.round(
                      today.diff(dateToCheck, "days", true)
                    );
                    return diff > 180 || diff < 30;
                  }
                  return false;
                },
                is: "swaJobOrderEndDateCheck",
                operator: "customValidation",
              },
            },
            {
              text: "Advertisement date has to be a Sunday.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.newspaperAdPlacedDate;
                  const sundayPaperExists =
                    formApi.getState().values.sundayPaperExists === "yes";
                  const errors =
                    formApi.getState().errors.newspaperAdPlacedDate;
                  if (!!value && !errors) {
                    const dayOfWeek = moment(value).day();
                    return dayOfWeek !== 0 && sundayPaperExists;
                  }
                  return false;
                },
                is: "swaJobOrderEndDateCheck",
                operator: "customValidation",
              },
            },
          ],
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: ["professional_occupation", "non_professional"],
              },
            },
          ],
          tooltip: `Enter the date of the first advertisement identified in MM/DD/YYYY format.
            <br/><br/><b><u>Note:</u></b> In accordance with 20 CFR 656.17(e)(1)(i)(B)(1), placing 
            an advertisement on two different Sundays in the newspaper of general circulation 
            in the area of intended employment most appropriate to the occupation and the workers 
            likely to apply for the job opportunity and most likely to bring responses from able, 
            willing, qualified, and available U.S. workers and, in accordance with 20 CFR 656.17(e)(1)(i)(B)(4), 
            if the job involved in the application requires experience and an advanced degree, and a 
            professional journal normally would be used to advertise the job opportunity, the employer 
            may, in lieu of one of the Sunday advertisements, place an advertisement in the 
            professional journal most likely to bring responses from able, willing, qualified, and available U.S. workers.`,
        },
        {
          isGroup: {
            code: "H.c.3",
            machine_name: "advertisementMethod",
            label:
              "Which of the following did the employer use to place the other advertisement for the job opportunity? (Choose only one) §",
            clearBtn: {
              clearSchema: [],
            },
            tooltip:
              "Indicate if the second advertisement was placed in a professional journal or newspaper by marking “Newspaper of general circulation,” “Professional Journal,” or “N/A.”",
            fields: [
              {
                widget: "radio",
                choice: "Newspaper of general circulation",
                value: "newspaper",
                validate: [
                  {
                    isRequired: "occupationType",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: ["professional_occupation", "non_professional"],
                    },
                  },
                ],
              },
              {
                widget: "radio",
                choice: "Professional journal",
                value: "professional-journal",
                validate: [
                  {
                    isRequired: "occupationType",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: ["professional_occupation", "non_professional"],
                    },
                  },
                ],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: "n/a",
                validate: [
                  {
                    isRequired: "occupationType",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: ["professional_occupation", "non_professional"],
                    },
                  },
                ],
              },
            ],
          },
        },
        {
          code: "H.c.3a",
          machine_name: "journalNameForAd",
          widget: "text",
          label:
            "Name of the newspaper or professional journal in which an advertisement was placed §",
          maxLength: 35,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: ["professional_occupation", "non_professional"],
              },
            },
          ],
          tooltip:
            "Enter the name of the professional journal or newspaper in which the second advertisement was placed.",
        },
        {
          code: "H.c.3b",
          machine_name: "journalAdPlacedDate",
          widget: "date",
          label: "Advertisement date §",
          warnings: [
            {
              text: "The recruitment steps must be conducted at least 30 days, but no more than 180 days, before filing the application.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.journalAdPlacedDate;
                  const errors = formApi.getState().errors.journalAdPlacedDate;
                  if (!!value && !errors) {
                    const today = moment();
                    const dateToCheck = moment(value);
                    const diff = Math.round(
                      today.diff(dateToCheck, "days", true)
                    );
                    return diff > 180 || diff < 30;
                  }
                  return false;
                },
                is: "swaJobOrderEndDateCheck",
                operator: "customValidation",
              },
            },
            {
              text: "Advertisement date has to be a Sunday.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.journalAdPlacedDate;
                  const bothAreNewspapers =
                    formApi.getState().values.advertisementMethod ===
                      "newspaper" &&
                    formApi.getState().values.sundayPaperExists === "yes";
                  const errors = formApi.getState().errors.journalAdPlacedDate;
                  if (!!value && !errors) {
                    const dayOfWeek = moment(value).day();
                    return dayOfWeek !== 0 && bothAreNewspapers;
                  }
                  return false;
                },
                is: "swaJobOrderEndDateCheck",
                operator: "customValidation",
              },
            },
            {
              text: "Newspaper advertisements must be two different Sundays.",
              isVisible: {
                when: (formApi) => {
                  const value = formApi.getState().values.journalAdPlacedDate;
                  const valueToCheck =
                    formApi.getState().values.newspaperAdPlacedDate;
                  const bothAreNewspapers =
                    formApi.getState().values.advertisementMethod ===
                      "newspaper" &&
                    formApi.getState().values.sundayPaperExists === "yes";

                  const errors = formApi.getState().errors.journalAdPlacedDate;
                  if (!!value && !errors) {
                    const sameDate = moment(value).isSame(valueToCheck);
                    return sameDate && bothAreNewspapers;
                  }
                  return false;
                },
                is: "swaJobOrderEndDateCheck",
                operator: "customValidation",
              },
            },
          ],
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: ["professional_occupation", "non_professional"],
              },
            },
          ],
          tooltip:
            "Enter the date of the second advertisement in MM/DD/YYYY format.",
        },
      ],
    },
  ],
};
